import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ArrowLeft, Mail, MailCheck } from "lucide-react";
import api, { describeAxiosError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateEmail } from "@/lib/validators";
import { useAuth } from "@/context/AuthContext";

/** Single-step "send reset link" page.
 *
 * The user types their email and we POST `/auth/forgot-password`. The
 * backend mails a link with a 1-hour token. We then render a static
 * "check your inbox" success card so the user can close this tab and
 * return via the email link itself (rather than re-entering anything).
 */
export default function ForgotPassword() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const sendLink = async (e) => {
    e?.preventDefault();
    const v = validateEmail(email);
    if (!v.ok) { toast.error(v.msg); return; }

    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Rate-limit (slowapi 429), network, or backend `{detail:...}`.
      toast.error(describeAxiosError(err, "Could not send reset link"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 grid place-items-center p-4">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded bg-blue-700 grid place-items-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">AXIORA</div>
            <div className="text-xs text-gray-500">Reset your password</div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-6">
          {!sent && (
            <form onSubmit={sendLink} className="space-y-4" data-testid="form-forgot">
              <div className="flex items-center gap-2 text-blue-700">
                <Mail className="w-4 h-4" />
                <h2 className="font-semibold">Send reset link</h2>
              </div>
              <p className="text-sm text-gray-600">
                Enter your account email and we&apos;ll send you a secure link
                to choose a new password. The link is valid for 1&nbsp;hour.
              </p>
              <div className="space-y-1">
                <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-gray-600">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="input-forgot-email"
                  required
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-blue-700 hover:bg-blue-800"
                data-testid="btn-send-reset-link"
              >
                {sending ? "Sending…" : "Send reset link"}
              </Button>
              <div className="pt-2 flex items-center justify-between text-xs">
                <Link to="/login" className="text-gray-500 hover:text-gray-900 inline-flex items-center gap-1" data-testid="link-back-login">
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
              </div>
            </form>
          )}

          {sent && (
            <div className="space-y-4" data-testid="forgot-sent">
              <div className="flex items-center gap-2 text-emerald-700">
                <MailCheck className="w-5 h-5" />
                <h2 className="font-semibold">Check your inbox</h2>
              </div>
              <p className="text-sm text-gray-600">
                If an account exists for <span className="font-mono text-gray-900">{email}</span>,
                a password-reset link has been sent. Open the email and click the
                <strong> Reset Password </strong>button to choose a new one.
              </p>
              <p className="text-xs text-gray-500">
                Don&apos;t see it? Check your spam / promotions folder. The link
                is valid for <strong>1 hour</strong>.
              </p>
              <div className="flex items-center justify-between pt-2 text-xs">
                <Link to="/login" className="text-gray-500 hover:text-gray-900 inline-flex items-center gap-1" data-testid="link-back-login-sent">
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
                <button
                  type="button"
                  onClick={() => { setSent(false); }}
                  className="text-blue-700 hover:underline"
                  data-testid="btn-send-again"
                >
                  Send to a different email
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Account locked? Contact your administrator.
        </p>
      </div>
    </div>
  );
}
